"use client";

import * as React from "react";
import { MoreHorizontal, Eye, Pencil, Trash2, GitBranch } from "lucide-react";
import { cn } from "../../../lib/utils";
import { MENU_ACTION_LABELS, kanbanTagVariants } from "./constants";
import { useKanbanCard, useKanbanBoard } from "./context";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../dropdown-menu";
import { IconButton } from "../icon-button";
import type { KanbanCardHeaderProps, KanbanMenuAction } from "./types";

const ACTION_ICONS: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  view: Eye,
  edit: Pencil,
  "copy-branch": GitBranch,
  delete: Trash2,
};

/** §5.20: priority reads as a `kanbanTag`, not a filled brand-tinted pill. */
const PRIORITY_TAG_COLOR: Record<string, "red" | "orange" | "blue"> = {
  high: "red",
  medium: "orange",
  low: "blue",
};

const KanbanCardHeader = React.forwardRef<
  HTMLDivElement,
  KanbanCardHeaderProps
>(({ className, ...props }, ref) => {
  const { card } = useKanbanCard();
  const { menuActions, onCardMenuAction } = useKanbanBoard();

  const hasMenu = menuActions.length > 0 && onCardMenuAction;

  return (
    <div
      ref={ref}
      className={cn("flex items-center justify-between gap-2", className)}
      {...props}
    >
      <div className="flex items-center gap-1.5 min-w-0">
        {card.typeBadge && (
          <span
            className={cn(kanbanTagVariants({ color: "default" }))}
            style={{ color: card.typeBadge.color }}
          >
            {card.typeBadge.label}
          </span>
        )}
        {card.priorityBadge ? (
          <span
            className={cn(kanbanTagVariants({ color: "default" }))}
            style={{ color: card.priorityBadge.color }}
          >
            {card.priorityBadge.label}
          </span>
        ) : card.priority ? (
          <span
            className={cn(
              kanbanTagVariants({
                color: PRIORITY_TAG_COLOR[card.priority] ?? "default",
              }),
            )}
          >
            {card.priority.charAt(0).toUpperCase() + card.priority.slice(1)}
          </span>
        ) : null}
      </div>

      <div className="flex items-center gap-1.5">
        {card.code && (
          <span className="font-mono text-xs tabular-nums text-text-tertiary">
            {card.code}
          </span>
        )}

        {hasMenu && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton
                size="sm"
                label="Card actions"
                tooltip={false}
                className="-mr-1"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal aria-hidden="true" />
              </IconButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {menuActions.map((action) => {
                const Icon = ACTION_ICONS[action];
                const label = MENU_ACTION_LABELS[action] || action;

                return (
                  <DropdownMenuItem
                    key={action}
                    onSelect={(e: Event) => {
                      e.stopPropagation();
                      onCardMenuAction?.(card, action as KanbanMenuAction);
                    }}
                    className={cn(
                      "whitespace-nowrap",
                      action === "delete" && "text-danger focus:text-danger",
                    )}
                  >
                    {Icon && <Icon className="mr-2 size-3.5" aria-hidden="true" />}
                    {label}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
});

KanbanCardHeader.displayName = "KanbanCardHeader";

export { KanbanCardHeader };
